import {
  type SmartCreationDraftContentV2,
  smartCreationStrategyTargetLane,
} from "../schemas/smart-creation";
import {
  type SmartCreationReviewGraph,
  type SmartCreationReviewWorkspace,
  SmartCreationReviewWorkspaceSchema,
} from "../schemas/smart-creation-review";
import { hash } from "./hashing";
import { reviewTargetIntent } from "./strategy-intent";

export function summaryFor(
  graph: SmartCreationReviewGraph,
  draftContent: SmartCreationDraftContentV2,
  endDate: string | null,
) {
  const excludedCampaignCount = graph.campaigns.filter(
    (campaign) => campaign.excluded.effective,
  ).length;
  const excludedTargetCount = graph.targets.filter((target) => target.excluded.effective).length;
  const totalDailyBudget = graph.campaigns.reduce(
    (total, campaign) =>
      campaign.excluded.effective ? total : total + campaign.dailyBudget.effective,
    0,
  );
  return {
    campaignCount: graph.campaigns.length,
    includedCampaignCount: graph.campaigns.length - excludedCampaignCount,
    excludedCampaignCount,
    adGroupCount: graph.adGroups.length,
    productAdCount: graph.adGroups.reduce((count, group) => count + group.products.length, 0),
    targetCount: graph.targets.length,
    includedTargetCount: graph.targets.length - excludedTargetCount,
    excludedTargetCount,
    negativeKeywordCount: graph.negatives.filter((negative) => negative.type === "keyword").length,
    negativeProductCount: graph.negatives.filter((negative) => negative.type === "product").length,
    totalDailyBudget,
    currencyCode: draftContent.currencyCode,
    startDate: draftContent.campaigns[0]?.startDate ?? "1970-01-01",
    endDate,
  };
}

export function contentHashFor(
  workspace: Omit<SmartCreationReviewWorkspace, "contentHash">,
): string {
  const { status: _status, ...reviewedContent } = workspace;
  return hash({ ...reviewedContent, contentHashSchemaVersion: 5 });
}

export function finalizeWorkspace(
  workspace: Omit<SmartCreationReviewWorkspace, "contentHash">,
): SmartCreationReviewWorkspace {
  // Coverage is evaluated against included Review objects, so excluding a whole
  // Campaign cannot silently erase an intent selected in Step 3.
  if (workspace.strategyIntentCoverage) {
    workspace.pendingStrategyLanes = [
      ...new Set(
        workspace.strategyIntentCoverage
          .filter(
            (coverage) =>
              !workspace.graph.targets.some((target) => {
                if (
                  target.excluded.effective ||
                  smartCreationStrategyTargetLane(
                    target.strategyKind,
                    reviewTargetIntent(target),
                  ) !== coverage.lane
                )
                  return false;
                const group = workspace.graph.adGroups.find(
                  (group) => group.ref.lineageKey === target.adGroupLineageKey,
                );
                if (
                  !group?.products.some((product) => coverage.productAsins.includes(product.asin))
                )
                  return false;
                return workspace.graph.campaigns.some(
                  (campaign) =>
                    campaign.ref.lineageKey === group.campaignLineageKey &&
                    !campaign.excluded.effective,
                );
              }),
          )
          .map((coverage) => coverage.lane),
      ),
    ];
  }
  const diagnostics = [
    ...workspace.diagnostics.filter((item) => !item.message.startsWith("Pending strategy: ")),
    ...(workspace.pendingStrategyLanes ?? []).map((lane) => ({
      severity: "warning" as const,
      code: "invalid_target" as const,
      blocking: false,
      entity: null,
      field: null,
      message: `Pending strategy: ${lane}. Add Targets or remove this intent before confirming.`,
    })),
  ];
  const finalized = { ...workspace, diagnostics };
  return SmartCreationReviewWorkspaceSchema.parse({
    ...finalized,
    contentHash: contentHashFor(finalized),
  });
}

export function withoutContentHash(
  workspace: SmartCreationReviewWorkspace,
): Omit<SmartCreationReviewWorkspace, "contentHash"> {
  const { contentHash: _contentHash, ...withoutHash } = workspace;
  return withoutHash;
}

export function cloneWorkspace(
  workspace: SmartCreationReviewWorkspace,
): SmartCreationReviewWorkspace {
  return structuredClone(workspace);
}
